import { Link, useSearchParams } from "react-router-dom";
import { useState, useMemo } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import StudentLink from "../../components/StudentLink";
import PhoneNumber from "../../components/PhoneNumber";
import Lang from "../../components/Lang.jsx";
import ButtonGroupObject from "../../components/ButtonGroupObject";
import VND from "../../components/VND.jsx";
import DateChoice from "../../components/DateChoice.jsx";
import useDBO from "../../hooks/useDBO.js";
import Loading from "../../Loading.jsx";
import Error from "../../components/Error.jsx";
import useCTX from "../../hooks/useCTX.js";

/**
 * Owed - Displays customers with outstanding tuition fees for an English center.
 * Sortable table with column headers, location filtering, and pagination.
 * Sort state and page state are persisted in URL search params.
 * @date 2026-04-21
 * @version 2.0
 * @author Tim, opencode + glm-5
 */
export default function Owed() {
  const { locations } = useCTX();
  const [location, setLocation] = useState(() => {
    if (!locations?.length) return null;
    const stored = localStorage.getItem("location");
    return stored && stored !== "2" && locations[stored]
      ? locations[stored]._id
      : locations[0]._id;
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromUrl = parseInt(searchParams.get("page") || "0", 10);
  const sortFromUrl = searchParams.get("sort") || "-owed";
  const [page, setPage] = useState(pageFromUrl);

  const rows = useDBO(`/customers/owed/${location}`);
  const data = rows.loading || rows.error ? [] : rows.get || [];

  const PAGE_SIZE = 50;

  const sortedRows = useMemo(() => {
    if (!sortFromUrl) return [...data];
    const dir = sortFromUrl.startsWith("-") ? -1 : 1;
    const field = sortFromUrl.replace("-", "");
    return [...data].sort((a, b) => {
      const aVal = a[field];
      const bVal = b[field];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return dir;
      if (bVal == null) return -dir;
      if (typeof aVal === "number" && typeof bVal === "number") return (aVal - bVal) * dir;
      return String(aVal).localeCompare(String(bVal)) * dir;
    });
  }, [data, sortFromUrl]);

  if (!location) return <Loading />;
  if (rows.loading) return <Loading />;
  if (rows.error) return <Error />;

  const totalPages = Math.ceil(sortedRows.length / PAGE_SIZE);
  const paginatedRows = sortedRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const updateParams = (updates) => {
    const params = Object.fromEntries(searchParams.entries());
    Object.assign(params, updates);
    setSearchParams(params);
  };

  const handleSort = (field) => {
    const currentField = sortFromUrl.replace("-", "");
    const currentDir = sortFromUrl.startsWith("-") ? "desc" : "asc";
    let newSort;
    if (currentField === field) {
      newSort = currentDir === "asc" ? `-${field}` : field;
    } else {
      newSort = `-${field}`;
    }
    updateParams({ sort: newSort, page: "0" });
    setPage(0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    updateParams({ page: String(newPage) });
  };

  const SortIcon = ({ field }) => {
    const currentField = sortFromUrl.replace("-", "");
    const currentDir = sortFromUrl.startsWith("-") ? "desc" : "asc";
    if (currentField !== field) {
      return <ArrowUpDown size={12} className="text-slate-300" />;
    }
    return currentDir === "desc" ? (
      <ArrowDown size={12} className="text-teal-600" />
    ) : (
      <ArrowUp size={12} className="text-teal-600" />
    );
  };

  const SortableHeader = ({ field, children, className = "" }) => (
    <th
      className={`px-4 py-3 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-slate-400 cursor-pointer hover:text-slate-600 transition-colors duration-150 select-none whitespace-nowrap ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1.5">
        {children}
        <SortIcon field={field} />
      </div>
    </th>
  );

  return (
    <div className="h-full flex flex-col" id="owed">
      <div className="flex items-center justify-between pb-4 shrink-0">
        <ButtonGroupObject
          items={locations}
          value={location}
          setValue={(loc) => {
            setLocation(loc);
            localStorage.setItem("location", locations.findIndex((l) => l._id === loc));
          }}
        />
        <div className="flex items-center gap-3">
          <h4 className="text-sm font-semibold text-slate-700">
            Total: <VND value={rows.reduce("owed")} />
          </h4>
          <span className="text-xs text-slate-400">
            {rows.length} customers
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-auto rounded-xl border border-slate-200/80 bg-white">
        <table className="w-full text-[0.8rem]">
          <thead className="bg-slate-50/80 border-b border-slate-200/80 sticky top-0 z-10">
            <tr>
              <SortableHeader field="name">
                <Lang w="name" />
              </SortableHeader>
              <SortableHeader field="phone">
                <Lang w="phone" />
              </SortableHeader>
              <SortableHeader field="owed">
                <Lang w="amount" />
              </SortableHeader>
              <th className="px-4 py-3 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-slate-400">
                <Lang w="students" />
              </th>
              <SortableHeader field="lastPayment">
                <Lang w="lastPayment" />
              </SortableHeader>
              <th className="px-4 py-3 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-slate-400">
                Hạn nộp tiền
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedRows.map((r) => (
              <tr
                key={r._id}
                className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50 transition-colors duration-150"
              >
                <td className="px-4 py-2.5">
                  <Link
                    className="text-slate-700 hover:text-teal-600 transition-colors duration-150"
                    to={"/customer/" + r._id}
                    style={{ color: r.name ? "#1e293b" : "#dc2626" }}
                  >
                    {r.name || "Không có tên"}
                  </Link>
                </td>
                <td className="px-4 py-2.5">
                  <a
                    href={`https://zalo.me/${r.phone?.replace("0", "84")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-600 hover:text-teal-600 transition-colors duration-150"
                  >
                    <PhoneNumber value={r.phone} />
                  </a>
                </td>
                <td className="px-4 py-2.5 text-right font-medium">
                  <span
                    className={
                      r.owed > 5000000
                        ? "text-red-600"
                        : r.owed > 2000000
                        ? "text-amber-600"
                        : "text-emerald-600"
                    }
                  >
                    <VND value={r.owed} />
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                    {r.students?.map((student) => (
                      <StudentLink
                        key={student._id}
                        student={student}
                        showClasses
                        tab="bills"
                        status
                        location
                      />
                    ))}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-slate-500">
                  {r.lastPayment ? new Date(r.lastPayment).fDate() : "—"}
                </td>
                <td className="px-4 py-2.5">
                  <DateChoice
                    value={r.followUp}
                    setValue={(followUp) => {
                      const row = rows.find((rr) => rr._id === r._id);
                      rows.updateById(r._id, { ...row, followUp });
                    }}
                    width={200}
                    fontSize={12}
                    noTextBox
                    noPast
                    url={`/customers/${r._id}/update`}
                    field="followUp"
                  />
                </td>
              </tr>
            ))}
            {paginatedRows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-slate-400 text-sm">
                  No customers with outstanding fees
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-3 shrink-0">
          <div className="text-xs text-slate-400">
            Page {page + 1} of {totalPages}
          </div>
          <div className="flex items-center gap-1">
            <PaginationBtn
              onClick={() => handlePageChange(0)}
              disabled={page < 1}
              label="First"
            />
            <PaginationBtn
              onClick={() => handlePageChange(page - 1)}
              disabled={page < 1}
              label="Prev"
            />
            <PaginationBtn
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages - 1}
              label="Next"
            />
            <PaginationBtn
              onClick={() => handlePageChange(totalPages - 1)}
              disabled={page >= totalPages - 1}
              label="Last"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function PaginationBtn({ onClick, disabled, label }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="px-3 py-1.5 text-xs font-medium rounded-full border border-slate-200 transition-all duration-150
        disabled:opacity-30 disabled:cursor-not-allowed
        hover:border-teal-300 hover:text-teal-700 hover:bg-teal-50 cursor-pointer"
    >
      {label}
    </button>
  );
}
